import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchProducts, fetchProviders, formatTT } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Filter, X } from "lucide-react";

const CATEGORIES = [
    { id: "personal", label: "Personal" },
    { id: "auto", label: "Auto" },
    { id: "mortgage", label: "Mortgage" },
    { id: "business", label: "Business / SME" },
    { id: "credit_card", label: "Credit Card" },
];

const Products = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [type, setType] = useState(params.get("type") || "");
    const [category, setCategory] = useState(params.get("category") || "");
    const [maxRate, setMaxRate] = useState(30);
    const [providers, setProviders] = useState([]);
    const [selectedProviders, setSelectedProviders] = useState([]);
    const [sort, setSort] = useState("rate_asc");
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchProviders().then(setProviders);
    }, []);

    useEffect(() => {
        setType(params.get("type") || "");
        setCategory(params.get("category") || "");
    }, [params]);

    useEffect(() => {
        setLoading(true);
        const q = {};
        if (type) q.type = type;
        if (category) q.category = category;
        if (maxRate < 30) q.max_rate = maxRate;
        if (sort) q.sort = sort;
        fetchProducts(q).then((items) => {
            const filtered = selectedProviders.length
                ? items.filter((p) => selectedProviders.includes(p.provider))
                : items;
            setProducts(filtered);
            setLoading(false);
        });
    }, [type, category, maxRate, sort, selectedProviders]);

    const toggleProvider = (p) =>
        setSelectedProviders((prev) =>
            prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
        );

    const clearFilters = () => {
        setType(""); setCategory(""); setMaxRate(30); setSelectedProviders([]);
        navigate("/products");
    };

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
            <div className="mb-10">
                <div className="label-overline text-[#718096] mb-2">Catalog</div>
                <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">
                    {category ? CATEGORIES.find(c => c.id === category)?.label : type === "credit_card" ? "Credit Cards" : type === "loan" ? "Loans" : "All Products"}
                </h1>
            </div>

            <div className="grid lg:grid-cols-12 gap-8">
                {/* Filters Sidebar */}
                <aside className="lg:col-span-3">
                    <div className="grid-card bg-white p-6 sticky top-24">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4" strokeWidth={1.5}/>
                                <span className="label-overline">Filters</span>
                            </div>
                            <button onClick={clearFilters} data-testid="clear-filters-btn" className="text-xs hover:underline">Clear</button>
                        </div>

                        <div className="mb-6">
                            <div className="label-overline mb-3">Type</div>
                            <div className="space-y-2">
                                {[
                                    { id: "", label: "All" },
                                    { id: "loan", label: "Loans" },
                                    { id: "credit_card", label: "Credit Cards" },
                                ].map((t) => (
                                    <label key={t.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="radio" name="type"
                                            checked={type === t.id}
                                            onChange={() => { setType(t.id); setCategory(""); }}
                                            data-testid={`filter-type-${t.id || "all"}`}
                                            className="accent-[#1E3A2B]"
                                        />
                                        {t.label}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {type !== "credit_card" && (
                            <div className="mb-6">
                                <div className="label-overline mb-3">Loan Category</div>
                                <div className="space-y-2">
                                    {CATEGORIES.filter(c => c.id !== "credit_card").map((c) => (
                                        <label key={c.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                            <input
                                                type="radio" name="category"
                                                checked={category === c.id}
                                                onChange={() => setCategory(c.id)}
                                                data-testid={`filter-cat-${c.id}`}
                                                className="accent-[#1E3A2B]"
                                            />
                                            {c.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mb-6">
                            <div className="label-overline mb-3">Max Interest Rate</div>
                            <div className="font-mono-body text-2xl mb-2">{maxRate}%</div>
                            <Slider
                                value={[maxRate]}
                                onValueChange={(v) => setMaxRate(v[0])}
                                min={5} max={30} step={0.5}
                                data-testid="filter-max-rate-slider"
                                className="my-3"
                            />
                        </div>

                        <div>
                            <div className="label-overline mb-3">Provider</div>
                            <div className="space-y-2">
                                {providers.map((p) => (
                                    <label key={p} className="flex items-center gap-2 cursor-pointer text-sm">
                                        <Checkbox
                                            checked={selectedProviders.includes(p)}
                                            onCheckedChange={() => toggleProvider(p)}
                                            data-testid={`filter-provider-${p.replace(/\s+/g, "-").toLowerCase()}`}
                                            className="rounded-none border-[#0B192C]/30 data-[state=checked]:bg-[#1E3A2B] data-[state=checked]:border-[#1E3A2B]"
                                        />
                                        {p}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Results */}
                <div className="lg:col-span-9">
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#0B192C]/10">
                        <div className="text-sm text-[#4A5568] font-mono-body" data-testid="results-count">
                            {loading ? "Loading…" : `${products.length} ${products.length === 1 ? "result" : "results"}`}
                        </div>
                        <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger className="w-48 rounded-none border-[#0B192C]/20 font-mono-body bg-white" data-testid="sort-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="rate_asc">Lowest Rate</SelectItem>
                                <SelectItem value="rate_desc">Highest Rate</SelectItem>
                                <SelectItem value="rating_desc">Top Rated</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {loading ? (
                        <div className="grid md:grid-cols-2 gap-4">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={`skeleton-${i}`} className="grid-card bg-white p-6 h-64 animate-pulse" />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="grid-card bg-white p-12 text-center">
                            <X className="w-8 h-8 mx-auto mb-3 text-[#718096]" strokeWidth={1.5}/>
                            <h3 className="font-serif-display text-2xl mb-2">Nothing matches</h3>
                            <p className="text-sm text-[#4A5568] mb-4">Try widening your filters.</p>
                            <button onClick={clearFilters} className="btn-primary px-5 py-2.5 label-overline">Reset filters</button>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-4">
                            {products.map((p) => <ProductCard key={p.id} product={p} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Products;
